import followUpTemplatesAPI from '../../api/followUpTemplates';
import { createAccountRequestGuard } from './utils/accountRequestGuard';

const fetchRequestGuard = createAccountRequestGuard();

export const state = {
  records: [],
  variables: [],
  items: {}, // { templateId: [items] }
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingItems: false,
    isCreatingItem: false,
    isUpdatingItem: false,
    isDeletingItem: false,
  },
};

export const getters = {
  getTemplates: $state => $state.records,
  getActiveTemplates: $state => $state.records.filter(tpl => tpl.active),
  getTemplate: $state => id => $state.records.find(tpl => tpl.id === id),
  getVariables: $state => $state.variables,
  getUIFlags: $state => $state.uiFlags,
  getItems: $state => templateId => $state.items[templateId] || [],
};

export const actions = {
  async fetch(
    { commit, state: localState, rootGetters = {} },
    { accountId, generation } = {}
  ) {
    const request = fetchRequestGuard.begin(localState, {
      accountId: accountId ?? rootGetters.getCurrentAccountId,
      generation,
    });
    const isCurrentRequest = () =>
      fetchRequestGuard.isCurrent(localState, request, rootGetters);
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await followUpTemplatesAPI.get();
      if (isCurrentRequest()) {
        commit('SET_TEMPLATES', response.data.payload);
      }
      return response.data.payload;
    } finally {
      if (isCurrentRequest()) {
        commit('SET_UI_FLAG', { isFetching: false });
      }
    }
  },

  async show({ commit }, templateId) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await followUpTemplatesAPI.show(templateId);
      commit('UPDATE_TEMPLATE', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async create({ commit }, { attachments = [], ...templateData }) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const response = await followUpTemplatesAPI.create(
        templateData,
        attachments
      );
      commit('ADD_TEMPLATE', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, attachments = [], ...templateData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const response = await followUpTemplatesAPI.update(
        id,
        templateData,
        attachments
      );
      commit('UPDATE_TEMPLATE', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async deleteAttachment({ commit }, { templateId, attachmentId }) {
    const response = await followUpTemplatesAPI.deleteAttachment(
      templateId,
      attachmentId
    );
    commit('UPDATE_TEMPLATE', response.data);
    return response.data;
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await followUpTemplatesAPI.delete(id);
      commit('DELETE_TEMPLATE', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async fetchVariables({ commit }) {
    const response = await followUpTemplatesAPI.getVariables();
    commit('SET_VARIABLES', response.data.variables);
  },

  async preview(_, { templateId, context }) {
    const response = await followUpTemplatesAPI.preview(templateId, context);
    return response.data;
  },

  // Items actions
  async fetchItems({ commit }, templateId) {
    commit('SET_UI_FLAG', { isFetchingItems: true });
    try {
      const response = await followUpTemplatesAPI.getItems(templateId);
      commit('SET_ITEMS', { templateId, items: response.data.payload });
      return response.data.payload;
    } finally {
      commit('SET_UI_FLAG', { isFetchingItems: false });
    }
  },

  async createItem({ commit }, { templateId, data, attachment }) {
    commit('SET_UI_FLAG', { isCreatingItem: true });
    try {
      const response = await followUpTemplatesAPI.createItem(
        templateId,
        data,
        attachment
      );
      commit('ADD_ITEM', { templateId, item: response.data });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingItem: false });
    }
  },

  async updateItem({ commit }, { templateId, itemId, data, attachment }) {
    commit('SET_UI_FLAG', { isUpdatingItem: true });
    try {
      const response = await followUpTemplatesAPI.updateItem(
        templateId,
        itemId,
        data,
        attachment
      );
      commit('UPDATE_ITEM', { templateId, item: response.data });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUpdatingItem: false });
    }
  },

  async deleteItem({ commit }, { templateId, itemId }) {
    commit('SET_UI_FLAG', { isDeletingItem: true });
    try {
      await followUpTemplatesAPI.deleteItem(templateId, itemId);
      commit('DELETE_ITEM', { templateId, itemId });
    } finally {
      commit('SET_UI_FLAG', { isDeletingItem: false });
    }
  },

  async reorderItems({ commit }, { templateId, items }) {
    const response = await followUpTemplatesAPI.reorderItems(templateId, items);
    commit('SET_ITEMS', { templateId, items: response.data.payload });
    return response.data.payload;
  },
};

export const mutations = {
  SET_TEMPLATES($state, templates) {
    $state.records = templates;
  },
  RESET_ACCOUNT_DATA($state) {
    $state.records = [];
    $state.variables = [];
    $state.items = {};
    $state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetchingItems: false,
      isCreatingItem: false,
      isUpdatingItem: false,
      isDeletingItem: false,
    };
  },
  ADD_TEMPLATE($state, template) {
    $state.records.push(template);
  },
  UPDATE_TEMPLATE($state, template) {
    const index = $state.records.findIndex(tpl => tpl.id === template.id);
    if (index !== -1) {
      $state.records.splice(index, 1, template);
    }
  },
  DELETE_TEMPLATE($state, id) {
    $state.records = $state.records.filter(tpl => tpl.id !== id);
  },
  SET_VARIABLES($state, variables) {
    $state.variables = variables;
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  SET_ITEMS($state, { templateId, items }) {
    $state.items = { ...$state.items, [templateId]: items };
  },
  ADD_ITEM($state, { templateId, item }) {
    const currentItems = $state.items[templateId] || [];
    $state.items = {
      ...$state.items,
      [templateId]: [...currentItems, item].sort(
        (a, b) => a.position - b.position
      ),
    };
  },
  UPDATE_ITEM($state, { templateId, item }) {
    const currentItems = $state.items[templateId] || [];
    const index = currentItems.findIndex(i => i.id === item.id);
    if (index !== -1) {
      const newItems = [...currentItems];
      newItems.splice(index, 1, item);
      $state.items = {
        ...$state.items,
        [templateId]: newItems.sort((a, b) => a.position - b.position),
      };
    }
  },
  DELETE_ITEM($state, { templateId, itemId }) {
    const currentItems = $state.items[templateId] || [];
    $state.items = {
      ...$state.items,
      [templateId]: currentItems.filter(i => i.id !== itemId),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
